import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import './main.css';
import ProductList from './ProductList';
import CategorySection from './CategorySection';

const CategoryPage = ({ customerDiscount, loggedInCustomerCode }) => {
  const { categoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryDescription, setCategoryDescription] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [heroImage, setHeroImage] = useState('');

  // Fetch all products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef);
        const querySnapshot = await getDocs(q);
        
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        setProducts(productsData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products by category whenever products or categoryName changes
  useEffect(() => {
    if (products.length === 0) return;

    // Decode the category name from the URL
    const decodedCategory = decodeURIComponent(categoryName);
    
    // Filter products that belong to this category
    const filteredProducts = products.filter(product => {
      if (!product.category) return false;
      
      // Split categories by comma and check if the current category is included
      const productCategories = product.category.split(',').map(cat => cat.trim());
      return productCategories.includes(decodedCategory);
    });
    
    setCategoryProducts(filteredProducts);
    
    // Set a description based on category name
    const descriptions = {
      'Memory Foam': 'Experience ultimate comfort with our premium memory foam products. Designed to contour perfectly to your body, providing exceptional support and pressure relief.',
      'Pillows': 'Elevate your sleep with our ergonomically designed pillows. Each pillow is crafted with precision to ensure proper neck alignment and maximum comfort throughout the night.',
      'Mattresses': 'Discover the perfect balance of comfort and support with our innovative mattress collection. Engineered for restful sleep and lasting durability.',
      'Toppers': 'Transform your existing mattress with our high-quality toppers. Add an extra layer of plush comfort and extend the life of your mattress.',
      'Custom Solutions': 'Tailored to your exact specifications, our custom foam solutions provide the perfect answer to your unique comfort needs.',
    };
    
    setCategoryDescription(descriptions[decodedCategory] || `Explore our collection of ${decodedCategory} products. Each item is crafted with premium materials and innovative design to ensure exceptional comfort and durability.`);
    
    // Select featured products (first 3 or all if less than 3)
    const featured = filteredProducts.slice(0, Math.min(3, filteredProducts.length));
    setFeaturedProducts(featured);
    
    // Set hero image from the first product with an image
    if (filteredProducts.length > 0) {
      const productWithImage = filteredProducts.find(product => product.image);
      if (productWithImage) {
        setHeroImage(productWithImage.image);
      }
    }
    
  }, [products, categoryName]);

  if (isLoading) {
    return <div className="loading-message">Loading Category Products...</div>;
  }

  const decodedCategory = decodeURIComponent(categoryName);

  return (
    <div className="category-page">
      {/* Hero Section */}
      <div className="category-hero" style={{ backgroundImage: heroImage ? `url(${heroImage})` : 'none' }}>
        <div className="category-hero-overlay"></div>
        <div className="category-hero-content">
          <h1>{decodedCategory}</h1>
          <p>{categoryDescription}</p>
        </div>
      </div>
      

      <div className="category-showcase">
        <h2 className="showcase-heading">Our <span>{decodedCategory} Collection</span></h2>
        {categoryProducts.length > 0 ? (
          <div className="products">
            <ProductList 
              initialLimit={12} 
              customerDiscount={customerDiscount} 
              initialSearchTerm="" 
              products={categoryProducts} 
            />
          </div>
        ) : (
          <div className="no-products-message">
            No products found in this category.
            <div className="back-to-categories">
              <Link to="/#categories" className="btn">Back to Categories</Link>
            </div>
          </div>
        )}
      </div>
      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <div className="featured-products-container">
          <h2 className="featured-heading">Featured <span>{decodedCategory} Products</span></h2>
          <div className="featured-products">
            {featuredProducts.map((product, index) => (
              <div key={product.id} className={`featured-product featured-product-${index + 1}`}>
                <div className="featured-product-image">
                  <img src={product.image} alt={product.name} />
                </div>
                <div className="featured-product-content">
                  <h3>{product.name}</h3>
                  <p className="featured-product-description">{product.description}</p>
                  <div className="featured-product-details">
                    {product.SKU && <span className="featured-product-sku">SKU: {product.SKU}</span>}
                    <Link to={`/products/${product.id}`} className="btn featured-product-btn">View Details</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Benefits Section */}
      <div className="category-benefits">
        <h2 className="benefits-heading">Why Choose Our <span>{decodedCategory}</span></h2>
        <div className="benefits-container">
          <div className="benefit">
            <div className="benefit-icon">
              <i className="fas fa-medal"></i>
            </div>
            <h3>Premium Quality</h3>
            <p>Crafted with the finest materials and rigorous quality control</p>
          </div>
          <div className="benefit">
            <div className="benefit-icon">
              <i className="fas fa-cog"></i>
            </div>
            <h3>Advanced Technology</h3>
            <p>Incorporating the latest innovations in foam manufacturing</p>
          </div>
          <div className="benefit">
            <div className="benefit-icon">
              <i className="fas fa-sync-alt"></i>
            </div>
            <h3>Durability</h3>
            <p>Designed to maintain performance and shape for years</p>
          </div>
          <div className="benefit">
            <div className="benefit-icon">
              <i className="fas fa-heart"></i>
            </div>
            <h3>Enhanced Comfort</h3>
            <p>Engineered to provide the ultimate comfort experience</p>
          </div>
        </div>
      </div>
      
      {/* Main Product Showcase */}
  
      
      {/* Related Categories */}
      <div className="related-categories-container">
        <h2 className="related-heading">Explore Other Categories</h2>
        <CategorySection />
      </div>
    </div>
  );
};

export default CategoryPage; 